/**
 * Knowledge Base — controlled values and sample articles.
 * Spec: docs/specs/knowledge-base.md
 */

export const KB_ROUTE = '/admin/support/kb';

export const ARTICLE_USE_OPTIONS = ['Answer Guide', 'Troubleshooting', 'Support Process'];

export const CATEGORY_OPTIONS = [
  'Account Access',
  'Billing & Subscriptions',
  'Technical Help',
  'General Support',
  'Appeal Intake',
  'Compliance Query',
];

export const KB_SUPPORTING_TEXT =
  'Internal answer guides and support help used when replying to tickets.';

/** §9 sample table + article body content from §15–§33. */
export function buildSampleKnowledgeArticles() {
  return [
    {
      articleId: 'KB-0001',
      title: 'Password reset email not received',
      category: 'Account Access',
      articleUse: 'Troubleshooting',
      keywords: ['password', 'reset', 'email', 'spam'],
      topic: 'Account login and password reset',
      lastUpdated: '05 Apr 2026',
      whenToUse:
        'Use this article when a customer says password reset emails are missing, delayed, expired, or not working as expected.',
      quickAnswer:
        'If a customer is not receiving the password reset email, first confirm the account email, ask them to check spam or junk folders, and then check whether reset email delivery is working normally before giving the next step.',
      guidanceSteps: [
        'Confirm the customer is using the correct account email.',
        'Ask whether the reset request was made more than once.',
        'Ask the customer to check spam, junk, and filtered folders.',
        'Check whether there is a current delivery issue affecting reset emails.',
        'If delivery failure is confirmed, update the customer before asking them to retry.',
        'Route the issue to the assigned team if it continues after the email checks are complete.',
      ],
      suggestedReply:
        'Hi, thanks for contacting support. Please confirm that you are using the correct email address for your account and check your spam or junk folder for the reset email. We are also checking whether there is a recent delivery issue affecting password reset messages. If needed, we will guide you through the next step.',
      relatedFiles: [
        { fileName: 'password-reset-checklist.pdf', fileType: 'PDF' },
        { fileName: 'reset-email-flow.png', fileType: 'Image' },
      ],
      articleHistory: [
        {
          version: 'v3',
          updatedBy: 'Support — ABC Name',
          updatedOn: '05 Apr 2026',
          summary: 'Added reset email delivery check guidance',
        },
        {
          version: 'v2',
          updatedBy: 'Development — ABC Name',
          updatedOn: '03 Apr 2026',
          summary: 'Updated suggested reply wording',
        },
        {
          version: 'v1',
          updatedBy: 'Support — ABC Name',
          updatedOn: '30 Mar 2026',
          summary: 'Initial article created',
        },
      ],
      archived: false,
    },
    {
      articleId: 'KB-0002',
      title: 'How business subscriptions are billed',
      category: 'Billing & Subscriptions',
      articleUse: 'Answer Guide',
      keywords: ['subscription', 'billing', 'invoice'],
      topic: 'Business subscription billing',
      lastUpdated: '02 Apr 2026',
      whenToUse:
        'Use this article when a business asks how subscriptions are billed or where invoices appear.',
      quickAnswer:
        'Business subscriptions are billed in recurring cycles. Invoice records are available in billing history for the account.',
      guidanceSteps: [
        'Confirm the business account and active subscription plan.',
        'Explain the recurring billing cycle for that plan.',
        'Point the business to billing history for invoices.',
        'If a charge looks incorrect, collect the invoice or transaction reference before escalating.',
      ],
      suggestedReply:
        'Hi, thanks for contacting support. Business subscriptions are billed on a recurring cycle for your plan, and invoices are available in your billing history. If a specific charge looks wrong, share the invoice or transaction reference and we will review it.',
      relatedFiles: [],
      articleHistory: [
        {
          version: 'v1',
          updatedBy: 'Support — ABC Name',
          updatedOn: '02 Apr 2026',
          summary: 'Initial article created',
        },
      ],
      archived: false,
    },
    {
      articleId: 'KB-0003',
      title: 'When to route a compliance query to the assigned team',
      category: 'Compliance Query',
      articleUse: 'Support Process',
      keywords: ['compliance', 'route', 'escalation'],
      topic: 'Compliance query routing',
      lastUpdated: '01 Apr 2026',
      whenToUse:
        'Use this article when deciding whether a ticket should be routed as a compliance query to the assigned team.',
      quickAnswer:
        'Route once the required evidence is confirmed and the issue meets compliance escalation criteria.',
      guidanceSteps: [
        'Confirm the record type and the concern raised.',
        'Check whether required evidence is available.',
        'Confirm the issue meets compliance escalation criteria.',
        'Route to the assigned compliance team and record the handoff in the ticket.',
      ],
      suggestedReply:
        'Hi, thanks for contacting support. We have reviewed your query and routed it to the assigned compliance team for further review. You will receive an update through this ticket.',
      relatedFiles: [],
      articleHistory: [
        {
          version: 'v1',
          updatedBy: 'Support — ABC Name',
          updatedOn: '01 Apr 2026',
          summary: 'Initial article created',
        },
      ],
      archived: false,
    },
    {
      articleId: 'KB-0004',
      title: 'Reply guide for removed review appeal requests',
      category: 'Appeal Intake',
      articleUse: 'Answer Guide',
      keywords: ['appeal', 'review', 'removed'],
      topic: 'Appeal intake replies',
      lastUpdated: '04 Apr 2026',
      whenToUse:
        'Use this article when replying to a requester about a removed review appeal request.',
      quickAnswer:
        'Use the approved reply wording, collect the appeal ID where available, and confirm the expected review timeline.',
      guidanceSteps: [
        'Confirm the appeal relates to a removed review.',
        'Collect the appeal ID if the requester has one.',
        'Use the approved reply wording.',
        'Confirm the expected review timeline without promising an outcome.',
      ],
      suggestedReply:
        'Hi, thanks for contacting support. We have received your request about a removed review appeal. Please share your appeal ID if you have one. Our team will review the request within the expected timeline and update you through this ticket.',
      relatedFiles: [],
      articleHistory: [
        {
          version: 'v1',
          updatedBy: 'Support — ABC Name',
          updatedOn: '04 Apr 2026',
          summary: 'Initial article created',
        },
      ],
      archived: false,
    },
    {
      articleId: 'KB-0005',
      title: 'Unable to submit website form',
      category: 'Technical Help',
      articleUse: 'Troubleshooting',
      keywords: ['form', 'submit', 'website', 'browser'],
      topic: 'Website form submission',
      lastUpdated: '28 Mar 2026',
      whenToUse:
        'Use this article when a user cannot submit a website form.',
      quickAnswer:
        'Check required fields and common browser issues, then capture clear reproduction steps before escalating.',
      guidanceSteps: [
        'Confirm which form and page the user was on.',
        'Ask whether required fields were completed.',
        'Ask the user to retry in another browser or after clearing cache if needed.',
        'Capture exact error messages and reproduction steps.',
        'Escalate with those details if the issue continues.',
      ],
      suggestedReply:
        'Hi, thanks for contacting support. Please confirm which form you were submitting and whether any required fields were left blank. If the issue continues, try another browser and share any error message you see so we can investigate further.',
      relatedFiles: [],
      articleHistory: [
        {
          version: 'v1',
          updatedBy: 'Support — ABC Name',
          updatedOn: '28 Mar 2026',
          summary: 'Initial article created',
        },
      ],
      archived: false,
    },
    {
      articleId: 'KB-0006',
      title: 'Handling duplicate charge enquiries',
      category: 'Billing & Subscriptions',
      articleUse: 'Support Process',
      keywords: ['duplicate', 'charge', 'refund'],
      topic: 'Duplicate charge handling',
      lastUpdated: '27 Mar 2026',
      whenToUse:
        'Use this article when a customer or business reports a possible duplicate charge.',
      quickAnswer:
        'Validate the duplicate transaction evidence, follow the refund process where confirmed, and tell the customer the expected settlement window.',
      guidanceSteps: [
        'Collect the transaction or invoice references for both charges.',
        'Confirm whether the charges are true duplicates.',
        'Follow the refund process when a duplicate is confirmed.',
        'Update the customer with the expected settlement window.',
      ],
      suggestedReply:
        'Hi, thanks for contacting support. Please share the transaction or invoice references for the charges in question. Once we confirm a duplicate, we will follow the refund process and let you know the expected settlement window.',
      relatedFiles: [],
      articleHistory: [
        {
          version: 'v1',
          updatedBy: 'Support — ABC Name',
          updatedOn: '27 Mar 2026',
          summary: 'Initial article created',
        },
      ],
      archived: false,
    },
  ];
}

export function findKnowledgeArticle(articles, articleId) {
  const id = decodeURIComponent(String(articleId || '')).trim();
  if (!id) return null;
  return (articles || []).find((row) => row.articleId === id) || null;
}

export function matchesKnowledgeSearch(article, searchTerm) {
  const term = String(searchTerm || '')
    .trim()
    .toLowerCase();
  if (!term) return true;
  const keywords = (article.keywords || []).join(' ').toLowerCase();
  return (
    String(article.title || '')
      .toLowerCase()
      .includes(term) ||
    keywords.includes(term) ||
    String(article.topic || '')
      .toLowerCase()
      .includes(term)
  );
}
